/**
 * Brand geometry — the single source of truth for the platform P-mark.
 *
 * The mark reproduces the ratified concept sheet (bottom-left concept,
 * pixel-measured from the original at 1024px and scaled by 0.3977 into this
 * viewBox; original coordinates in comments as `[orig …]`):
 *
 *   - `p-stem` — a tall, dead-straight editorial bar (width 18) with two
 *     wedge flares in point symmetry: top-left and bottom-right, each a flat
 *     cut with a concave return into the body. Not calligraphic — the body
 *     edges are straight.
 *   - `p-bowl` — a detached SOLID sail: a near-vertical chord on the left,
 *     one big smooth arc on the right, and thin tips that hook back LEFT
 *     toward the stem. It floats far right of the stem (the gap is ~3.3
 *     stem widths), its top aligned with the stem's top.
 *   - `p-dot` — the round full stop (operator addition, »P.«). It sits on
 *     the TEXT baseline. Its x-position is ELASTIC: at rest it sits under
 *     the bowl's belly; a long app name pushes it right (the dot terminates
 *     the name, never left of the bowl).
 *
 * The app name sits in the wide gap between stem and bowl, tucked against
 * the stem, on a baseline at ~65% of the stem's height — the stem descends
 * far below it, exactly as in the original. Every coordinate lives here in
 * ONE user-unit space (`VIEW`), shared by the custom element, the animation
 * timelines, and the standalone SVG assets.
 */

/** The mark's coordinate system (user units). Width grows with the app name. */
export const VIEW = {
  /** Top of stem and bowl. [orig y=582] */
  capTop: 16,
  /** The shared TEXT baseline — app name and dot sit on it. [orig y=812] */
  baseline: 107.5,
  /** The stem's bottom cut — far below the baseline. [orig y=934] */
  tail: 156,
  /** ViewBox height. */
  height: 168,
  /** ViewBox width with NO app name (the bare »P.«). */
  minWidth: 160,
} as const;

/**
 * The stem: a straight bar [orig x 163..208 → 30..48, width 18], flat top
 * cut with the LEFT wedge flare [orig tip x=137 → 20], straight body edges,
 * and the point-symmetric RIGHT wedge flare at the bottom
 * [orig tip x=232 → 57.5] above a flat bottom cut.
 */
export const STEM_PATH = [
  "M 20 16", // top-left flare tip
  "L 48 16", // flat top cut
  "L 48 141.5", // straight right edge [orig flare starts y≈898]
  "C 48.5 146 51 150.5 57.5 155", // concave flare out to the bottom-right tip
  "L 30 156", // flat bottom cut
  "L 30 26.5", // straight left edge [orig flare ends y≈608]
  "C 23.5 18 21.5 17 20 16", // concave return up into the flare tip
  "Z",
].join(" ");

/**
 * The bowl — one solid sail. Outer edge: a single smooth arc from top tip
 * through the belly [orig x=436 → 138.7] to the bottom tip. Inner edge: a
 * near-vertical chord [orig x=382 → 117.2] whose ends curve back LEFT into
 * the hooking tips [orig top (358,584), bottom (362,776)].
 */
export const BOWL_PATH = [
  "M 108 16.5", // top tip (hooks left, level with the stem top)
  "C 124 18 138.7 33.5 138.7 52", // outer arc to the belly
  "C 138.7 71.5 129 87 109.2 93.2", // outer arc down to the bottom tip
  "C 112.5 91 115 88 117.2 83.5", // tip hooks back up to the chord
  "L 117.2 27", // the chord — straight, vertical
  "C 114.5 24 111 20 108 16.5", // chord top curves out to the tip
  "Z",
].join(" ");

/** The full-stop dot. */
export const DOT = {
  r: 6,
  /** cy — the dot SITS on the text baseline. */
  cy: VIEW.baseline - 6,
  /** Leftmost allowed cx: under the bowl's belly (the bare »P.« position). */
  minCx: 139,
  /** Horizontal gap between the app name's end and the dot. */
  gapAfterName: 6,
} as const;

/**
 * The SLOT — the wide gap between stem and bowl where the app name sits,
 * tucked against the stem [orig text start x=222, 14px right of the stem],
 * on the shared baseline. Faithful to the original, the name is small
 * relative to the P (cap ≈ 10% of stem height) — the mark is meant to be
 * used large.
 */
export const SLOT = {
  x: 54,
  baseline: VIEW.baseline,
  capHeight: 13.5, // [orig ≈ 34px]
  /** Font size that yields ~capHeight for Fraunces (cap ≈ 0.72 em). */
  fontSize: 19,
} as const;

/**
 * Named anchor points — first-class animation handles. Rendered as
 * zero-size `.anchor-*` nodes so tooling can query real coordinates.
 */
export const ANCHORS = {
  /** Baseline at the stem — where the composition starts. */
  baselineStart: { x: 30, y: VIEW.baseline },
  /** Where the app name begins (slot origin). */
  slotStart: { x: SLOT.x, y: SLOT.baseline },
  /** The bowl's top tip — the hinge the bowl swings from. */
  bowlHinge: { x: 108, y: 16.5 },
  /** The bowl's bottom tip — hooks toward the name below. */
  bowlTip: { x: 109.2, y: 93.2 },
  /** The dot's home with no app name (»P.«). */
  dotHome: { x: DOT.minCx, y: DOT.cy },
} as const;

export interface Point {
  x: number;
  y: number;
}

/** Dot cx for a given app-name width (0 = no name → the bare »P.«). */
export function dotCx(nameWidth: number): number {
  if (nameWidth <= 0) return DOT.minCx;
  return Math.max(DOT.minCx, SLOT.x + nameWidth + DOT.gapAfterName);
}

/** ViewBox width for a given app-name width. */
export function viewWidth(nameWidth: number): number {
  return Math.max(VIEW.minWidth, Math.ceil(dotCx(nameWidth) + DOT.r + 14));
}

/**
 * Fallback advance-width estimate (user units) for environments without
 * SVG text measurement (happy-dom, SSR). Tuned for Fraunces at SLOT.fontSize;
 * the element re-measures with `getComputedTextLength()` in real browsers.
 */
export function estimateNameWidth(name: string): number {
  const em = SLOT.fontSize;
  let width = 0;
  for (const ch of name) {
    if (/[A-Z]/.test(ch)) width += 0.72 * em;
    else if (/[mw]/.test(ch)) width += 0.86 * em;
    else if (/[ilíìj.,' ]/.test(ch)) width += 0.28 * em;
    else if (/[ftr]/.test(ch)) width += 0.38 * em;
    else width += 0.53 * em;
  }
  return width;
}
